/**
 * ThemeProvider — owns the active top-level theme.
 *
 * Phase 2 of docs/backlog/theming-system.md. Responsibilities:
 *   1. hold the current ThemeId (localStorage-backed; Phase 3 layers the per-user
 *      server pref on top via useUserUiPrefs);
 *   2. reflect it onto <html data-theme="…"> so CSS `[data-theme="…"]` overrides
 *      and the canvas (useCanvasEngine reads document.documentElement) pick it up;
 *   3. dispatch a `theme-changed` window event so the canvas re-renders (its
 *      colours are drawn in JS, not CSS, so a class flip alone won't repaint it).
 *
 * With one theme this is effectively a no-op visually, but it makes the switch
 * machinery live so Phase 3 only has to call setTheme.
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';
import { THEMES, DEFAULT_THEME, isThemeId, type ThemeId } from '../lib/themes';

const STORAGE_KEY = 'roll1:theme';

function readStoredTheme(): ThemeId {
  if (typeof window === 'undefined') return DEFAULT_THEME;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isThemeId(stored)) return stored;
  } catch {
    /* localStorage blocked (private mode / disabled) — fall back to default */
  }
  return DEFAULT_THEME;
}

interface ThemeContextValue {
  theme: ThemeId;
  setTheme: (id: ThemeId) => void;
  themes: typeof THEMES;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(readStoredTheme);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore persistence failure — in-memory theme still applies */
    }
    // Canvas paints colours in JS and only re-reads data-theme on this event.
    window.dispatchEvent(new Event('theme-changed'));
  }, [theme]);

  const setTheme = useCallback((id: ThemeId) => {
    setThemeState(isThemeId(id) ? id : DEFAULT_THEME);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, themes: THEMES }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return ctx;
}
